import { useCallback, useEffect, useRef, useState } from "react";

export interface UsePdfViewerOptions {
  src: string | null;
  enableZoom?: boolean;
  initialScale?: number;
  minScale?: number;
  maxScale?: number;
  /** Optional scroll container for page tracking */
  containerRef?: React.RefObject<HTMLElement | null>;
}

export interface UsePdfViewerReturn {
  numPages: number | null;
  scale: number;
  setScale: (scale: number | ((prev: number) => number)) => void;
  onDocumentLoadSuccess: (data: { numPages: number }) => void;
  onDocumentLoadError: (error: Error) => void;
  openInNewTab: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  /** Current page based on scroll position (1-indexed). */
  currentPage: number;
  /** Assign to each rendered page wrapper: ref={setPageRef(index)} */
  setPageRef: (index: number) => (el: HTMLDivElement | null) => void;
}

/**
 * usePdfViewer
 *
 * A React hook that powers a lightweight PDF viewing experience with optional zoom
 * and scroll-based current page tracking. Consumers should:
 * - Provide an optional scroll `containerRef` that wraps the rendered pages.
 * - Attach the returned `setPageRef(index)` to each page wrapper in order (0-indexed).
 *
 * Current page tracking algorithm (1-indexed results):
 * - On scroll/resize, compute the vertical stride between the first two pages. If only
 *   one page exists, approximate the stride as first.offsetHeight + marginBottom.
 * - Compute the container's visual center: scrollTop + clientHeight / 2.
 * - Map the center to a page index with floor((center - firstTop) / stride),
 *   clamped to [0, numPages - 1], then +1 to get the 1-indexed page number.
 *
 * Why requestAnimationFrame is used here:
 * - It schedules the calculation so it runs at most once per screen refresh. This merges
 *   many scroll and resize events into a single calculation, which reduces redundant work
 *   and helps avoid visual stutter.
 * - It runs after the browser has updated layout and before it draws the next frame, so
 *   measurements like offsetTop and offsetHeight are up to date without forcing layout.
 * - It is naturally throttled in background tabs, which can reduce power usage.
 *
 * Mount-time scheduling:
 * - We schedule once immediately and again on the next animation frame. The second run
 *   accounts for late layout changes (for example images or fonts loading), which improves
 *   the accuracy of the initial current page detection.
 *
 * Additional details:
 * - A schedule function is exposed so new page references can request a calculation
 *   without bypassing the per-frame coalescing behavior.
 * - Event listeners are removed and any pending requestAnimationFrame callbacks are
 *   canceled when the component unmounts.
 */
export const usePdfViewer = ({
  src,
  enableZoom = true,
  initialScale = 1,
  minScale = 0.5,
  maxScale = 3,
  containerRef,
}: UsePdfViewerOptions): UsePdfViewerReturn => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(initialScale);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const scheduleComputeRef = useRef<() => void>(() => {});

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
    },
    [],
  );

  const onDocumentLoadError = useCallback((_error: Error) => {
    setNumPages(null);
  }, []);

  const openInNewTab = useCallback(() => {
    if (src) {
      window.open(src, "_blank");
    }
  }, [src]);

  const zoomIn = useCallback(() => {
    if (enableZoom) {
      setScale((prev) => Math.min(prev + 0.2, maxScale));
    }
  }, [enableZoom, maxScale]);

  const zoomOut = useCallback(() => {
    if (enableZoom) {
      setScale((prev) => Math.max(prev - 0.2, minScale));
    }
  }, [enableZoom, minScale]);

  const resetZoom = useCallback(() => {
    if (enableZoom) {
      setScale(initialScale);
    }
  }, [enableZoom, initialScale]);

  // Keep pageRefs array length in sync
  useEffect(() => {
    pageRefs.current = Array.from({ length: numPages || 0 }).map(() => null);
  }, [numPages]);

  /*
   * Scroll-based current page calculation
   * - Assumes uniform page height and spacing (typical for PDF rendering).
   * - Derives the vertical stride either from the first two pages' offsetTop
   *   or from the first page's height plus its margin-bottom when only one page exists.
   * - Computes the page whose center is closest to the container's visual center.
   * - Scheduling uses requestAnimationFrame; if a frame is already scheduled, additional
   *   scroll or resize events do not schedule more work, so the calculation runs at most
   *   once per screen refresh.
   */
  useEffect(() => {
    const root = containerRef?.current;
    if (!root || !numPages) return;

    let rafId: number | null = null;

    const compute = () => {
      rafId = null;
      const first = pageRefs.current[0];
      if (!first) return;
      const second = pageRefs.current[1];

      const firstTop = first.offsetTop;
      let stride: number;
      if (second) {
        stride = second.offsetTop - firstTop;
      } else {
        const styles = window.getComputedStyle(first);
        const mb = Number.parseFloat(styles.marginBottom || "0");
        stride = first.offsetHeight + mb;
      }
      if (!stride || stride <= 0) return;

      const center = root.scrollTop + root.clientHeight / 2;
      const index = Math.max(
        0,
        Math.min(numPages - 1, Math.floor((center - firstTop) / stride)),
      );
      const next = index + 1;
      if (next !== currentPage) setCurrentPage(next);
    };

    const schedule = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(compute);
    };

    root.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    // Schedule twice: once now, and once on the next animation frame to ensure layout is settled
    schedule();
    const id = window.requestAnimationFrame(() => schedule());

    // Expose the scheduler so that ref attachments can trigger an initial calculation
    scheduleComputeRef.current = schedule;

    return () => {
      root.removeEventListener("scroll", schedule as EventListener);
      window.removeEventListener("resize", schedule as EventListener);
      if (rafId !== null) cancelAnimationFrame(rafId);
      cancelAnimationFrame(id);
      scheduleComputeRef.current = () => {};
    };
  }, [containerRef?.current, numPages, currentPage]);

  /**
   * Returns a ref setter for the page at `index`.
   * Call this on each page wrapper: `ref={setPageRef(index)}`.
   * When refs attach, we schedule a compute so `currentPage` is correct ASAP.
   */
  const setPageRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      pageRefs.current[index] = el;
      // schedule a compute when refs attach so current page is calculated
      scheduleComputeRef.current();
    },
    [],
  );

  return {
    currentPage,
    numPages,
    scale,
    setScale,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    openInNewTab,
    zoomIn,
    zoomOut,
    resetZoom,
    setPageRef,
  };
};
